import cn from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TextField } from '@react-md/form';
import { FeatureContent } from '@components/feature-content';
import { FormField, NonFieldErrors } from '@components/form-field';
import { SubmitButton } from '@components/submit-button';
import type { FetchBaseQueryError } from '@services/df/baseApi';
import { setApiErrors } from '@utils';
import { useValidation } from '@hooks';
import { createTwoFactorValidation } from '../form-validation';
import { AuthSectionText } from '../components';
import { twoFactorId } from '../helpers';
import { TwoFactorForm } from '../types';
import { TwoFactorFormId } from '../constants';

interface UseTwoFactorFormProps {
    request: (data: TwoFactorForm) => Promise<void>;
    onSuccess?: () => void;
}

const useTwoFactorForm = ({ request, onSuccess }: UseTwoFactorFormProps) => {
    const validationSchema = useValidation(createTwoFactorValidation);
    const formApi = useForm<TwoFactorForm>({
        defaultValues: {
            code: '',
        },
        resolver: yupResolver(validationSchema),
    });

    const {
        setError,
        formState: { isSubmitting: isTokenObtaining },
    } = formApi;

    const submit: SubmitHandler<TwoFactorForm> = async form => {
        try {
            const response = await request(form);

            onSuccess?.();
        } catch (error) {
            const errors = (error as FetchBaseQueryError).data;
            setApiErrors(setError, errors);
        }
    };
    return {
        formApi,
        isTokenObtaining,
        submit,
    };
};

interface AuthTwoFactorFormProps extends UseTwoFactorFormProps {
    twoFactorType?: string;
}

export const AuthTwoFactorForm: React.FC<AuthTwoFactorFormProps> = ({
    children,
    twoFactorType,
    request,
    onSuccess,
}) => {
    const { formApi, isTokenObtaining, submit } = useTwoFactorForm({ request, onSuccess });

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = formApi;

    const { t } = useTranslation();

    return (
        <>
            <form
                id={TwoFactorFormId}
                className={cn('fieldset-grid-cell', 'fieldset-grid')}
                onSubmit={handleSubmit(submit)}
            >
                {twoFactorType === 'app' ? (
                    <AuthSectionText>
                        {t(
                            'messages.openAuthenticator',
                            'Open the authenticator app you linked during setup. ' +
                                'The code is displayed directly within the app and refreshes periodically.',
                        )}
                    </AuthSectionText>
                ) : twoFactorType === 'email' ? (
                    <AuthSectionText>
                        {t(
                            'messages.codeIsSent',
                            'The code is sent. Please check your inbox and enter the code sent to your email.',
                        )}
                    </AuthSectionText>
                ) : null}

                <FormField
                    prefix={TwoFactorFormId}
                    name="code"
                    control={control}
                    component={TextField}
                    label={t('labels.code', 'Code')}
                />

                <NonFieldErrors errors={errors} />
            </form>

            <div className={cn('emulated-flex-gap')}>
                <SubmitButton id={twoFactorId('send_code')} form={TwoFactorFormId} isSending={isTokenObtaining}>
                    <FeatureContent
                        contentKey={twoFactorId('send_code')}
                        fallback={t('buttons.sendCode', 'Send code')}
                    />
                </SubmitButton>
            </div>
        </>
    );
};
